import type { IUserService } from '../api.interface';
import type { UserProfile, BalanceInfo, AudioPreferences } from '../types';
import { StorageService, StorageKeys } from '../storage.service';
import { simulateLatency } from './latency';
import { INITIAL_BALANCE, DEFAULT_CURRENCY, DEFAULT_USERNAME, DEFAULT_AVATAR } from '../../game/constants';

const DEFAULT_PROFILE: UserProfile = {
  username: DEFAULT_USERNAME,
  avatar: DEFAULT_AVATAR,
};

const DEFAULT_AUDIO: AudioPreferences = {
  musicEnabled: true,
  effectsEnabled: true,
};

export class MockUserService implements IUserService {
  async getProfile(): Promise<UserProfile> {
    await simulateLatency();
    return StorageService.getOrDefault<UserProfile>(StorageKeys.PROFILE, DEFAULT_PROFILE) as UserProfile;
  }

  async updateProfile(profile: UserProfile): Promise<UserProfile> {
    await simulateLatency();
    StorageService.set(StorageKeys.PROFILE, profile);
    return profile;
  }

  async getBalance(): Promise<BalanceInfo> {
    await simulateLatency();
    const amount = StorageService.getOrDefault<number>(StorageKeys.BALANCE, INITIAL_BALANCE);
    return { amount, currency: DEFAULT_CURRENCY };
  }

  async creditBalance(amount: number): Promise<BalanceInfo> {
    await simulateLatency();
    const current = StorageService.getOrDefault<number>(StorageKeys.BALANCE, INITIAL_BALANCE);
    const updated = current + amount;
    StorageService.set(StorageKeys.BALANCE, updated);
    return { amount: updated, currency: DEFAULT_CURRENCY };
  }

  async debitBalance(amount: number): Promise<BalanceInfo> {
    await simulateLatency();
    const current = StorageService.getOrDefault<number>(StorageKeys.BALANCE, INITIAL_BALANCE);
    if (amount > current) {
      throw new Error('Insufficient balance');
    }
    const updated = current - amount;
    StorageService.set(StorageKeys.BALANCE, updated);
    return { amount: updated, currency: DEFAULT_CURRENCY };
  }

  async getAudioPreferences(): Promise<AudioPreferences> {
    await simulateLatency();
    return StorageService.getOrDefault<AudioPreferences>(StorageKeys.AUDIO_PREFS, DEFAULT_AUDIO) as AudioPreferences;
  }

  async updateAudioPreferences(prefs: AudioPreferences): Promise<AudioPreferences> {
    await simulateLatency();
    StorageService.set(StorageKeys.AUDIO_PREFS, prefs);
    return prefs;
  }
}
